import { createSelector } from 'reselect';

import FilteringService from '../services/filtering.service';
import { CoreoAttribute, CoreoCollection, CoreoCollectionItem, CoreoForm, CoreoMapLayer, CoreoProjectFeatures, CoreoRecord, CoreoRecordFilter, CoreoRecordSyncStatus } from '../types';
import { LayerInitialConfig } from '../types/forms.types';
import { StaticLayersRegistry } from '../services/map-layer-static-registry.service';

import { Feature, Geometry } from 'geojson';
import { MapDataLayer } from '../services/maps/maps-data-layer';
import { isBaseStyleLocked, mapboxMapStyles } from '../services/geometry.service';
import * as appSelectors from './app/app.selectors';
import * as authSelectors from './auth/auth.selectors';
import { evaluateConditions } from './forms/forms.reducer';
import * as formsSelectors from './forms/forms.selectors';
import * as formsSupportSelectors from './formsSupport/formsSupport.selectors';
import { MapInstanceState, MapLayerState, MapState } from './map/map.reducer';
import * as mapSelectors from './map/map.selectors';
import { OfflineMapsState } from './offlineMaps/offlineMaps.reducer';
import * as offlineMapsSelectors from './offlineMaps/offlineMaps.selectors';
import * as recordsSelectors from './records/records.selectors';
import { ApplicationState } from './reducers';
import { MapFeaturesService } from '../services/map-features.service';

export const getAuthState = (state: ApplicationState) => state.auth;
export const getAuthError = createSelector(getAuthState, authSelectors.getAuthError);
export const getAuthInProgress = createSelector(getAuthState, authSelectors.getAuthInProgress);
export const getAuthVerifyError = createSelector(getAuthState, authSelectors.getAuthVerifyError);
export const getAuthVerifyInProgress = createSelector(getAuthState, authSelectors.getAuthVerifyInProgress);
export const getAuthIsAuth = createSelector(getAuthState, authSelectors.getAuthIsAuth);
export const getAuthToken = createSelector(getAuthState, authSelectors.getAuthToken);
export const getAuthUser = createSelector(getAuthState, authSelectors.getAuthUser);
export const getAuthUserImageUrl = createSelector(getAuthState, authSelectors.getAuthUserImageUrl);
export const getAuthUserId = createSelector(getAuthState, authSelectors.getAuthUserId);
export const getAuthSignupProjectId = createSelector(getAuthState, authSelectors.getAuthSignupProjectId);

export const getAppState = (state: ApplicationState) => state.app;
export const getAppReady = createSelector(getAppState, appSelectors.getAppReady);
export const getAppSettings = createSelector(getAppState, appSelectors.getAppSettings);
export const getAppNetworkConnectionType = createSelector(getAppState, appSelectors.getAppNetworkConnectionType);
export const getAppNetworkConnected = createSelector(getAppState, appSelectors.getAppNetworkConnected);
export const getAppIsInstalled = (id: number) => createSelector(getAppState, appSelectors.getAppIsInstalled(id));
export const getAppStatus = (id: number) => createSelector(getAppState, appSelectors.getAppStatus(id));
export const getApps = createSelector(getAppState, appSelectors.getApps);
export const getAvailableApps = createSelector(getAppState, appSelectors.getAvailableApps);
export const getAppsCount = createSelector(getAppState, appSelectors.getAppsCount);
export const getCurrentAppStatus = createSelector(getAppState, appSelectors.getCurrentAppStatus);
export const getCurrentAppWelcomeLastSeen = createSelector(getAppState, appSelectors.getCurrentAppWelcomeLastSeen);
export const getCurrentAppLastSync = createSelector(getAppState, appSelectors.getCurrentAppLastSync);
export const getCurrentAppSyncState = createSelector(getAppState, appSelectors.getCurrentAppSyncState);
export const getAppMediaDownloadState = (id: number) => createSelector(getAppState, appSelectors.getAppMediaDownloadState(id));
export const getAppAttachmentDownloadState = (id: number) => createSelector(getAppState, appSelectors.getAppAttachmentDownloadState(id));
export const getAppMapDownloadState = (id: number) => createSelector(getAppState, appSelectors.getAppMapDownloadState(id));
export const getAppAttachmentsShouldDownload = createSelector(getAppState, appSelectors.getAppAttachmentsShouldDownload);
export const getAppAttachmentsShouldDownloadForApp = (id: number) => createSelector(getAppState, appSelectors.getAppAttachmentsShouldDownloadForApp(id));
export const getAppFormGroups = createSelector(getAppState, appSelectors.getAppFormGroups);
export const getAppIsUkhab = createSelector(getAppState, appSelectors.getAppIsUkhab);

export const getApp = createSelector(getAppState, appSelectors.getApp);
export const getAppId = createSelector(getAppState, appSelectors.getAppId);
export const getAppName = createSelector(getAppState, appSelectors.getAppName);
export const getAppProjectId = createSelector(getAppState, appSelectors.getAppProjectId);
export const getAppForms = createSelector(getAppState, appSelectors.getAppForms);
export const getAppFormsWithGeometry = createSelector(getAppState, appSelectors.getAppFormsWithGeometry);
export const getAppFormsWithGeometryTypes = (types: string[]) => createSelector(getAppState, appSelectors.getAppFormsWithGeometryTypes(types));
export const getAppParentForms = createSelector(getAppState, appSelectors.getAppParentForms);
export const getAppMediaTags = createSelector(getAppState, appSelectors.getAppMediaTags);
export const getAppMediaItems = createSelector(getAppState, appSelectors.getAppMediaItems);
export const getAppCollections = createSelector(getAppState, appSelectors.getAppCollections);
export const getAppGeometricCollections = createSelector(getAppState, appSelectors.getAppGeometricCollections);
export const getAppGeometricCollectionsForMap = createSelector(getAppState, appSelectors.getAppGeometricCollectionsForMap);
export const getAppCollection = (id: number) => createSelector(getAppState, appSelectors.getAppCollection(id));
export const getAppFeatures = createSelector(getAppState, appSelectors.getAppFeatures);
export const getAppFeatureIsEnabled = (f: keyof CoreoProjectFeatures) => createSelector(getAppState, appSelectors.getAppFeatureIsEnabled(f));
export const getAppAttributes = createSelector(getAppState, appSelectors.getAppAttributes);
export const getAppAttributesForFilter = createSelector(getAppState, appSelectors.getAppAttributesForFilter);
export const getAppPages = createSelector(getAppState, appSelectors.getAppPages);
export const getAppPagesForMenu = createSelector(getAppState, appSelectors.getAppPagesForMenu);
export const getAppTitleForRecord = (record: CoreoRecord) => createSelector(getAppState, appSelectors.getAppTitleForRecord(record));
export const getAppDescriptionForRecord = (record: CoreoRecord, label = false) => createSelector(getAppState, appSelectors.getAppDescriptionForRecord(record, label));
export const getAppDescriptionAttributes = createSelector(getAppState, appSelectors.getAppDescriptionAttributes);
export const getAppPage = (id: number) => createSelector(getAppState, appSelectors.getAppPage(id));
export const getAppStates = createSelector(getAppState, appSelectors.getAppStates);
export const getAppDefaultState = createSelector(getAppState, appSelectors.getAppDefaultState);
export const getAppForm = (id: number) => createSelector(getAppState, appSelectors.getAppForm(id));
export const getAppAttributesForForm = (id: number) => createSelector(getAppState, appSelectors.getAppAttributesForForm(id));
export const getAppAssociationAttributesForForm = (id: number) => createSelector(getAppState, appSelectors.getAppAssociationAttributesForForm(id));
export const getAppAttributesForDisplay = (id: number) => createSelector(getAppState, appSelectors.getAppAttributesForDisplay(id));
export const getAppTitleAttributeForForm = (id: number) => createSelector(getAppState, appSelectors.getAppTitleAttributeForForm(id));
export const getAppDescriptionAttributeForForm = (id: number) => createSelector(getAppState, appSelectors.getAppDescriptionAttributeForForm(id));
export const getAppAttributesForCollection = (id: number) => createSelector(getAppState, appSelectors.getAppAttributesForCollection(id));
export const getAppLastSync = (id: number) => createSelector(getAppState, appSelectors.getAppLastSync(id));
export const getAppMapLayers = createSelector(getAppState, appSelectors.getAppMapLayers);
export const getAppMapLayer = (id: number) => createSelector(getAppState, appSelectors.getAppMapLayer(id));
export const getAppBounds = createSelector(getAppState, appSelectors.getAppBounds);
export const getAppBoundsBoundingBox = createSelector(getAppState, appSelectors.getAppBoundsBoundingBox);
export const getAppShouldHideUsernames = createSelector(getAppState, appSelectors.getAppShouldHideUsernames);

// export const getAppMap = id => createSelector(getAppState, appSelectors.getAppMap(id));
export const getAppEnvironment = createSelector(getAppState, appSelectors.getAppEnvironment);
export const getAppConfig = createSelector(getAppState, appSelectors.getAppConfig);
export const getAppConfigAdminUrl = createSelector(getAppState, appSelectors.getAppConfigAdminUrl);
export const getAppConfigMapboxToken = createSelector(getAppState, appSelectors.getAppConfigMapboxToken);
export const getAppConfigMapsUrl = createSelector(getAppState, appSelectors.getAppConfigMapsUrl);
export const getAppConfigApiUrl = createSelector(getAppState, appSelectors.getAppConfigApiUrl);
export const getAppConfigEnvironmentName = createSelector(getAppState, appSelectors.getAppConfigEnvironmentName);
export const getAppConfigBingToken = createSelector(getAppState, appSelectors.getAppConfigBingToken);
export const getAppLocalDatabaseName = createSelector(getAppState, appSelectors.getLocalDatabaseName);
export const getAppIsDev = createSelector(getAppState, appSelectors.getAppIsDev);

export const getAppIcon = createSelector(getAppState, appSelectors.getAppIcon);
export const getAppTutorialShown = createSelector(getAppState, appSelectors.getAppTutorialShown);
export const getAppWelcomePageId = createSelector(getAppState, appSelectors.getAppWelcomePageId);
// export const getAppFreeTrialExpired = createSelector(getAppState, appSelectors.getAppFreeTrialExpired);
export const getAppHasGeometry = createSelector(getAppState, appSelectors.getAppHasGeometry);
export const getAppShowMap = createSelector(getAppState, appSelectors.getAppShowMap);

export const getFormsState = (state: ApplicationState) => state.forms;
export const getFormsForms = createSelector(getFormsState, formsSelectors.getFormsForms);
export const getFormsCurrentForm = createSelector(getFormsState, formsSelectors.getFormsCurrentForm);
export const getFormsCurrentFormId = createSelector(getFormsState, formsSelectors.getFormsCurrentFormId);
export const getFormsCurrentFormHasAnyGeometry = createSelector(getFormsState, formsSelectors.getFormsCurrentFormHasAnyGeometry);
export const getFormsForm = uuid => createSelector(getFormsState, formsSelectors.getFormsForm(uuid));
export const getFormsFormSections = uuid => createSelector(getFormsState, formsSelectors.getFormsFormSections(uuid));
export const getFormsFormBlocks = uuid => createSelector(getFormsState, formsSelectors.getFormsFormBlocks(uuid));
export const getFormsFormRecord = uuid => createSelector(getFormsState, formsSelectors.getFormsFormRecord(uuid));
export const getFormsFormDirty = uuid => createSelector(getFormsState, formsSelectors.getFormsFormDirty(uuid));
export const getFormsFormSectionId = uuid => createSelector(getFormsState, formsSelectors.getFormsFormSectionId(uuid));
export const getFormsFormNextSectionId = uuid => createSelector(getFormsState, formsSelectors.getFormsFormNextSectionId(uuid));
export const getFormsFormPrevSectionId = uuid => createSelector(getFormsState, formsSelectors.getFormsFormPrevSectionId(uuid));
export const getFormsFormIsReady = uuid => createSelector(getFormsState, formsSelectors.getFormsFormIsReady(uuid));
export const getFormsFormIsReadyToBeReady = uuid => createSelector(getFormsState, formsSelectors.getFormsFormIsReadyToBeReady(uuid));

export const getFormsSupportState = (state: ApplicationState) => state.formsSupport;
export const getFormsSupportCache = createSelector(getFormsSupportState, formsSupportSelectors.getFormsSupportCache);
export const getFormsSupportFormCache = formId => createSelector(getFormsSupportState, formsSupportSelectors.getFormsSupportFormCache(formId));
export const getFormsSupportGeometrySettings = createSelector(getFormsSupportState, formsSupportSelectors.getFormsSupportGeometrySettings);
export const getFormsSupportAttributeState = <T = any>(attributeId: string) => createSelector(getFormsSupportState, formsSupportSelectors.getFormsSupportAttributeState<T>(attributeId));
export const getFormsSupportRestoreMediaAttributeId = createSelector(getFormsSupportState, formsSupportSelectors.getFormsSupportRestoreMediaAttributeId);
export const getFormsSupportRestoreMediaAttachmentId = createSelector(getFormsSupportState, formsSupportSelectors.getFormsSupportRestoreMediaAttachmentId);
export const getFormsSupportRestoreMediaUrl = createSelector(getFormsSupportState, formsSupportSelectors.getFormsSupportRestoreMediaUrl);

export const getRecordsState = (state: ApplicationState) => state.records;
export const getRecordsFilter = createSelector(getRecordsState, recordsSelectors.getRecordFilter);
export const getRecordsLoading = createSelector(getRecordsState, recordsSelectors.getRecordsLoading);
export const getRecordsMapLoading = createSelector(getRecordsState, recordsSelectors.getRecordsMapLoading);
export const getRecordsRecord = createSelector(getRecordsState, recordsSelectors.getRecordsRecord);

export const getMapState = (state: ApplicationState): MapState => state.map;
export const getMapBaseStyleId = createSelector(getMapState, mapSelectors.getMapBaseStyleId);
export const getMapShowAppBounds = createSelector(getMapState, mapSelectors.getMapShowAppBounds);
export const getMapShowScaleBar = createSelector(getMapState, mapSelectors.getMapShowScaleBar);

export const getOfflineMapsState = (state: ApplicationState): OfflineMapsState => state.offlineMaps;
export const getOfflineMapsMaps = createSelector(getOfflineMapsState, offlineMapsSelectors.getOfflineMapsMaps);
export const getOfflineMapsAmbientEnabled = createSelector(getOfflineMapsState, offlineMapsSelectors.getOfflineMapsAmbientEnabled);
export const getOfflineMapsTutorialShown = createSelector(getOfflineMapsState, offlineMapsSelectors.getOfflineMapsTutorialShown);
export const getOfflineMapsMap = (uuid: string) => createSelector(getOfflineMapsState, offlineMapsSelectors.getOfflineMapsMap(uuid));

const createFormFilter = (publicFormIds, privateFormIds, userId) => {
  if (privateFormIds.length === 0) {
    if (publicFormIds.length === 0) {
      return {};
    }
    return {
      surveyId: {
        in: publicFormIds
      }
    };
  }

  const privateFormsFilter = {
    surveyId: {
      in: privateFormIds,
    },
    userId
  }

  if (publicFormIds.length === 0) {
    return privateFormsFilter;
  }

  return {
    or: [
      privateFormsFilter,
      {
        surveyId: {
          in: publicFormIds.filter(id => !privateFormIds.includes(id))
        }
      }]
  }
}

export const getRecordsBaseFilter = createSelector(getAppForms, getAuthUserId, (appForms: CoreoForm[], userId: number) => {
  const formIds = appForms.map(f => f.id);
  const selectedPrivateFormIds = appForms.filter(f => f.private && formIds.includes(f.id)).map(f => f.id);
  const selectedPublicFormIds = formIds.filter(id => !selectedPrivateFormIds.includes(id));
  return createFormFilter(selectedPublicFormIds, selectedPrivateFormIds, userId);
});

export const getRecordsCoreoFilter = createSelector(getRecordsFilter, getAppForms, getAuthUserId, (filter: CoreoRecordFilter, appForms: CoreoForm[], authUserId: number) => {
  const { customFilters, customOperator, states, forms, userId, to, from } = filter;
  const result: any = {};

  const selectedFormIds = forms.length ? forms : appForms.map(f => f.id);

  const selectedPrivateFormIds = appForms.filter(f => f.private && selectedFormIds.includes(f.id)).map(f => f.id);
  const selectedPublicFormIds = selectedFormIds.filter(id => !selectedPrivateFormIds.includes(id));

  if (states && states.length > 0) {
    result.state = {
      in: filter.states
    };
  }

  result['and'] = result['and'] || [];

  result['and'].push(createFormFilter(selectedPublicFormIds, selectedPrivateFormIds, authUserId));

  // if (forms && forms.length > 0) {
  //   result.surveyId = {
  //     in: filter.forms
  //   };
  // }

  if (userId) {
    result.userId = userId;
  }

  // From and To
  if (to && from) {
    result.createdAt = {
      between: [new Date(from).toISOString(), new Date(to).toISOString()]
    };
  } else if (from) {
    result.createdAt = {
      gte: new Date(from).toISOString()
    };
  } else if (to) {
    result.createdAt = {
      lte: new Date(to).toISOString()
    };
  }

  // If not custom operators, return
  if (customFilters.length === 0) {
    return result;
  }

  const customSelectors = [];
  for (const custom of customFilters.filter(c => FilteringService.instance.customFilterIsValid(c))) {
    const { operator, value, attribute: { path, type }, formId } = custom;
    let att = {};
    let parsedValue: any = value;
    if (Array.isArray(value)) {
      parsedValue = [...value];
    }
    if (type === 'multiselect' && operator.multiple) {
      const selectors = [];
      for (const val of parsedValue) {
        selectors.push({
          data: {
            [path]: {
              like: '%"' + val + '"%'
            }
          }
        });
      }
      const [op1, op2] = operator.id.split(':'); //
      if (typeof op2 !== 'undefined') {
        customSelectors.push({
          [op1]: { // if op2 is present, op1 is 'not'
            [op2]: selectors
          }
        })
      } else {
        customSelectors.push({
          [op1]: selectors
        })
      }
    }
    else {
      if (operator.id.indexOf(':') !== -1) {
        const [op, v] = operator.id.split(':');
        parsedValue = v === 'true' ? true : v === 'false' ? false : (v === 'null' ? null : v);
        att = { [op]: parsedValue }
      } else {
        att = { [operator.id]: value }
      }
      customSelectors.push({
        surveyId: formId,
        data: {
          [path]: att
        }
      });
    }
  }
  if (customSelectors.length) {
    if (customOperator === 'not') {
      result['and'] = customSelectors.map(c => ({ not: c }));
    } else {
      result['' + customOperator] = customSelectors;
    }
  }
  return result;
});

export const getShouldShowTutorial = createSelector(getAuthIsAuth, getAppTutorialShown, (isAuth, tutorialShown) => isAuth && !tutorialShown);

// export const getPendingRecordsForActiveApp = createSelector(
//   getAppId,
//   getRecordsPending,
//   (projectId, pending) => pending.filter(p => p.projectId === projectId)
// );

/**
 * Returns the records to display of the currently loaded record,
 * evaluating any conditionals present in the attributes to ensure
 * only displaying the attributes that are relevant to the record
 */
export const getRecordsRecordAttributes = createSelector(
  getRecordsRecord,
  getAppAttributes,
  (record, attributes) =>
    appSelectors.filterAttributesForForm(record.surveyId, attributes)
      .filter((attribute: CoreoAttribute, _i: number, formAttributes: CoreoAttribute[]) => evaluateConditions(attribute.conditions, formAttributes, record)
      ));

export const getRecordAttributes = (record: CoreoRecord) => createSelector(getAppAttributes,
  (attributes: CoreoAttribute[]) =>
    appSelectors.filterAttributesForForm(record.surveyId, attributes)
      .filter((attribute: CoreoAttribute, _i: number, formAttributes: CoreoAttribute[]) => evaluateConditions(attribute.conditions, formAttributes, record)
      ));

export const sortCollectionItems = (items: CoreoCollectionItem[], collection: CoreoCollection) => createSelector(getAppAttributesForCollection(collection.id), (attributes) => {
  switch (collection.sortMode) {
    case 'alphabetical': {
      items.sort((a, b) => {
        const aValue = String(a.value).toLowerCase();
        const bValue = String(b.value).toLowerCase();
        if (aValue < bValue) {
          return -1;
        }
        else if (aValue > bValue) {
          return 1;
        } else {
          return 0;
        }
      });
      break;
    }
    case 'attribute': {
      const sortAttribute = attributes.find(a => a.id === collection.sortAttributeId);
      // Fallback to sort order
      if (!sortAttribute) {
        items.sort((a, b) => a.sort - b.sort);
        break;
      }

      const numericComparison = sortAttribute.type === 'float' || sortAttribute.type === 'integer';

      items.sort((a, b) => {
        const aData = a.data ? a.data[sortAttribute.path] : null;
        const bData = b.data ? b.data[sortAttribute.path] : null;

        const aValue = numericComparison ? parseFloat(aData) : String(aData).toLowerCase();
        const bValue = numericComparison ? parseFloat(bData) : String(bData).toLowerCase();
        if (aValue < bValue) {
          return -1;
        }
        else if (aValue > bValue) {
          return 1;
        } else {
          return 0;
        }
      });
      break;
    }
    default: {
      items.sort((a, b) => a.sort - b.sort);
      break;
    }
  }
  return items;
});

const formToMapLayerState = (form: CoreoForm, state: Partial<MapLayerState> = {}): MapLayerState => ({
  id: `records_${form.id}`,
  layerType: 'records',
  sourceId: form.id,
  enabled: form.mapVisible,
  locked: false,
  ...state
});

export const collectionToMapLayerState = (collection: CoreoCollection, state: Partial<MapLayerState> = {}): MapLayerState => ({
  id: `collection_${collection.id}`,
  layerType: 'collection',
  sourceId: collection.id,
  enabled: collection.mapVisible,
  ...state,
});

const mapLayerToMapLayerState = (layer: CoreoMapLayer, state: Partial<MapLayerState> = {}): MapLayerState => ({
  id: `custom_${layer.id}`,
  layerType: 'custom',
  sourceId: layer.id,
  enabled: layer.visible,
  ...state
});

export const applyInitialMapLayerConfig = (state: MapInstanceState, config: LayerInitialConfig[] = []) => {
  for (const layer of [...state.dataLayers, ...state.layers]) {
    const match = config.find(c => c.type === layer.layerType && c.id === layer.sourceId);
    layer.enabled = typeof match !== 'undefined' || layer.enabled;
  }
}

const permittedBaseStyleId = (state: ApplicationState, baseStyleId: MapInstanceState['baseStyleId']) => {
  const features = appSelectors.getApp(getAppState(state))?.features;
  return isBaseStyleLocked(features, baseStyleId) ? mapboxMapStyles[0].id : baseStyleId;
};

export const getProjectMapBaseInstanceState = (id: string, state: ApplicationState) => {
  const forms = getAppFormsWithGeometry(state);
  const collections = getAppGeometricCollectionsForMap(state);
  const mapLayers = getAppMapLayers(state);
  const mapState = getMapState(state);
  const projectId = getAppId(state);
  const bounds = getAppBounds(state);
  // Building New State
  let dataLayers: MapLayerState[] = [];
  const layers: MapLayerState[] = [];

  for (const form of [...forms].sort((a, b) => a.mapSort - b.mapSort)) {
    dataLayers.push(formToMapLayerState(form));
  }

  const orderMap: Map<string, number> = new Map<string, number>();

  for (const collection of collections) {
    const collectionLayer = collectionToMapLayerState(collection);
    collectionLayer.enabled = !!collectionLayer.enabled;
    layers.push(collectionLayer);
    orderMap.set(collectionLayer.id, collection.mapSort);
  }

  for (const layer of mapLayers) {
    const mapLayerLayer = mapLayerToMapLayerState(layer);
    layers.push(mapLayerLayer);
    orderMap.set(mapLayerLayer.id, layer.sort ?? 1000);
  }

  layers.sort((a, b) => orderMap.get(a.id) - orderMap.get(b.id));

  const output: MapInstanceState = {
    id,
    baseStyleId: permittedBaseStyleId(state, mapState.baseStyleId),
    showAppBounds: !!bounds && mapState.showAppBounds,
    showScaleBar: mapState.showScaleBar,
    geometryOpacity: mapState.geometryOpacity,
    ukhabSymbology: mapState.ukhabSymbology,
    layers,
    dataLayers,
    projectId
  };
  return output;
};

export const getProjectMapInstanceState = (id: string) => createSelector(getMapState, (mapState) => {
  if (!(id in mapState.maps)) {
    return null;
  }
  const output: MapInstanceState = {
    id,
    baseStyleId: mapState.baseStyleId,
    showAppBounds: mapState.showAppBounds,
    showScaleBar: mapState.showScaleBar,
    geometryOpacity: mapState.geometryOpacity,
    ukhabSymbology: mapState.ukhabSymbology,
    ...mapState.maps[id]
  };
  return output;
});

export const consolidateMapState = (state: MapInstanceState) => createSelector(getAppFormsWithGeometry, getAppGeometricCollectionsForMap, getAppMapLayers, (forms, collections, mapLayers) => {
  const projectFormIds = new Set([...forms.map(form => form.id)]);

  // Remove any non existent data layers
  const dataLayers = state.dataLayers.filter(layer => projectFormIds.has(layer.sourceId));

  // Remove any non existent layers
  const layers = state.layers.filter(layer => {
    if (layer.layerType === 'collection') {
      return typeof collections.find(collection => collection.id === layer.sourceId) !== 'undefined';
    }
    if (layer.layerType === 'custom') {
      return typeof mapLayers.find(mapLayer => mapLayer.id === layer.sourceId) !== 'undefined';
    }
    if (layer.layerType === 'static') {
      return StaticLayersRegistry.instance.getLayerById(layer.id) !== null;
    }
    return false;
  });

  // Find missing layers
  const newDataLayers = forms.filter(form => dataLayers.findIndex(d => d.sourceId === form.id) === -1).map(form => formToMapLayerState(form));
  const newLayers: MapLayerState[] = [];

  for (const collection of collections) {
    if (typeof layers.find(l => l.layerType === 'collection' && l.sourceId === collection.id) === 'undefined') {
      const newCollectionLayer = collectionToMapLayerState(collection);
      newCollectionLayer.enabled = false;
      newLayers.push(newCollectionLayer);
    }
  }
  for (const mapLayer of mapLayers) {
    if (typeof layers.find(l => l.layerType === 'custom' && l.sourceId === mapLayer.id) === 'undefined') {
      const newMapLayerLayer = mapLayerToMapLayerState(mapLayer);
      newMapLayerLayer.enabled = false;
      newLayers.push(newMapLayerLayer);
    }
  }

  StaticLayersRegistry.instance.updateEnabledSets();
  const enabledStaticLayers = StaticLayersRegistry.instance.getEnabledLayers();

  for (const staticLayer of enabledStaticLayers) {
    if (!layers.find(l => l.id === staticLayer.id)) {
      newLayers.push({
        id: staticLayer.id,
        layerType: 'static',
        sourceId: 0,
        enabled: false,
        locked: false,
        hidden: false
      });
    }
  }

  const removed = (state.dataLayers.length - dataLayers.length) + (state.layers.length - layers.length);

  const updatedState = {
    ...state,
    dataLayers: [...dataLayers, ...newDataLayers],
    layers: [...layers, ...newLayers]
  };

  return {
    state: updatedState,
    newLayers,
    newDataLayers,
    removed
  };

});

export const getFilterableAttributes = createSelector(getAppAttributesForFilter, getRecordsFilter, (attributes, filter) => {
  if (filter.forms.length !== 1) {
    return [];
  }

  // return attributes.filter(a => a.filterable && a.surveyId === filter.forms[0]);
  return [...attributes.filter(a => a.surveyId === filter.forms[0])].sort((a, b) => a.order - b.order);
});

export interface Coreov1PendingRecord {
  data: any;
  geometry: any;
  id: number;
  projectId: number;
  surveyId: number;
  updatedAt: string;
  pendingState: number;
  formId: number;
  attachments: any[];
};


export const getV1PendingRecords = (state: ApplicationState) => {
  return (state.records as any)?.pending ?? [] as Coreov1PendingRecord[];
}

export const getV1PendingRecordsCount = createSelector(getV1PendingRecords, p => p.length);

export const getFormsFormSupportingRecords = createSelector(getAppForms, getFormsState, async (appForms, forms) => {
  const features: Feature<Geometry>[] = [];

  const activeForm = forms.forms[forms.forms.length - 1];
  const activeRecord = activeForm?.record;
  const stylers: Map<number, (features: Feature<Geometry>[]) => Feature<Geometry>[]> = new Map();

  for (const appForm of appForms) {
    stylers.set(appForm.id, await MapFeaturesService.instance.createFeatureStyler(appForm));
  }

  let tmpId = -1;

  const processSupportingRecord = (form: CoreoForm, record: CoreoRecord) => {
    const { associates, geometry, surveyId, id, syncState } = record;
    if (geometry && syncState !== CoreoRecordSyncStatus.PENDING_DELETE && !((record === activeRecord) || (id && id === activeRecord.id))) {

      let localId = tmpId--;
      const feature = stylers.get(surveyId)([{
        type: 'Feature',
        id: localId,
        properties: {
          ...record,
          id: localId
        },
        geometry
      }])[0];
      features.push(feature);
    }

    for (const k in associates) {
      const attributeId = Number(k);
      for (let i = 0; i < associates[attributeId].length; i++) {
        // Check if this is the active receord
        if (associates[k][i].formId === activeForm.id &&
          (attributeId === activeForm.subformAttributeId) &&
          (i === activeForm.subformIdx)) {
          continue;
        }
        processSupportingRecord(form, associates[k][i]);
      }
    }
  }

  for (const form of forms.forms) {
    processSupportingRecord(form, form.record)
  }
  return features;
});


export const getFormsFormSupportingRecordsLayer = createSelector(getAppForms, getFormsFormSupportingRecords, async (appForms, features)  => {
  const layer = new MapDataLayer();
  for (const appForm of appForms) {
    layer.addForm(appForm);
  }
  const fts = await features;
  layer.setFeatures(fts);
  return layer;
});

export const getUserCanDeleteFormRecord = (record: CoreoRecord) => createSelector(getAuthUser, getApp, getAppForms, (user, app, forms) => {
  if (app.membership === 'admin') {
    return true;
  }
  // Find the form
  const form = forms.find(f => f.id === record.surveyId);
  return form && form.allowOwnRecordDelete && user.userId === record.userId;
});

export const getUserCanEditFormRecord = (record: CoreoRecord) => createSelector(getAuthUser, getApp, getAppForms, (user, app, forms) => {
  if (record.userId === user.userId || app.membership === 'admin') {
    return true;
  }
  const form = forms.find(f => f.id === record.surveyId);
  return form && form.allowMemberUpdate;
});

export const getFormChildSurveysToKeepInBounds = (id: number) => createSelector(getAppAttributesForForm(id), getAppAttributes, (formAttributes, appAttributes) => {
  const childSurveyIds = formAttributes.filter(a => a.questionType === 'child').map(a => a.associatedSurveyId);
  const childSurveyGeometryQuestions = appAttributes
    .filter(a => childSurveyIds.includes(a.surveyId))
    .filter(a => (a.config?.hasOwnProperty('enforceParentBounds') && a.config.enforceParentBounds));

  if (childSurveyGeometryQuestions.length > 0) {
    return childSurveyGeometryQuestions.map(q => q.surveyId);
  }

  return [];
});
